import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import PATH_PAGE from '@/router/path';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import { Link } from 'react-router-dom';
import { PlusIcon, Server, Terminal } from 'lucide-react';

const GPUInstanceOverview = () => {
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { withDemoPrefix } = useDemoPath();

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">GPU Instance</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage GPU instances with SSH access
          </p>
        </div>
        <Link to={withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.create(orgId, workspaceId))}>
          <Button startIcon={<PlusIcon className="size-4" />}>
            Create Instance
          </Button>
        </Link>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="size-5" />
              Dedicated GPU
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Get exclusive access to powerful GPU instances for your workloads.
              Perfect for training, inference, and development.
            </CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Terminal className="size-5" />
              SSH Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Connect directly to your instance via SSH. Full root access with
              your own private key for secure authentication.
            </CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlusIcon className="size-5" />
              Get Started
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CardDescription className="mb-4">
              Create your first GPU instance and start using it within minutes.
            </CardDescription>
            <Link to={withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.create(orgId, workspaceId))}>
              <Button variant="outline" className="w-full">
                Create Instance
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-3 text-muted-foreground">
            <li>
              <span className="text-foreground font-medium">Select instance type</span>
              {' '}- Choose the GPU configuration that fits your needs
            </li>
            <li>
              <span className="text-foreground font-medium">Configure storage</span>
              {' '}- Set volume size and root filesystem size
            </li>
            <li>
              <span className="text-foreground font-medium">Create instance</span>
              {' '}- Click create and wait for your instance to start
            </li>
            <li>
              <span className="text-foreground font-medium">Download SSH key</span>
              {' '}- Save the private key (shown only once!)
            </li>
            <li>
              <span className="text-foreground font-medium">Connect via SSH</span>
              {' '}- Use the provided host and port to connect
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default GPUInstanceOverview;
